import React from 'react';
import { Button, Card, Col, Empty, Row, Spin, Typography } from 'antd';
import dayjs from 'dayjs';

import StoreCreditCardForm from '../../../components/billings/StoreCreditCardForm';
import { useGetCreditCardsQuery } from '../../../store/api/billingsApi';
import { messageType, showMessages } from '../../../helpers/message';

import { cardsGridColumn } from '../../../assets/styles';
import { black, white } from '../../../assets/colors';
import './StoredCreditCardCard.less';
import {useAppSelector} from '../../../hooks/useStore';
import {selectActiveAccount} from '../../../store/slices/userSlice';

const { Text } = Typography;

interface StoredCreditCardCardProps {
    setRefetchStoredCard: (value: boolean) => void;
}

const StoredCreditCardCard = ({ setRefetchStoredCard }: StoredCreditCardCardProps) => {
    const activeAccount = useAppSelector(selectActiveAccount);

    const { data: creditCards, isLoading: loading, isError: error, refetch } = useGetCreditCardsQuery({
        accountID: activeAccount?.id,
        page: 1,
        perPage: 10
    });

    React.useEffect(() => {
        if (error) {
            showMessages('An error occurred while fetching credit card data', messageType.error);
        }
    }, [error]);

    const [visible, setVisible] = React.useState(false);

    const onCancel = () => {
        setVisible(false);
    }

    const onSubmit = () => {
        setVisible(false);
        setRefetchStoredCard(true);
        refetch();
    }

    const getFormText = () => {
        return creditCards && creditCards.data.length !== 0 ? 'Update credit card' : 'Add credit card';
    }

    const getRenderComponent = () => {
        if (creditCards === undefined) {
            return <Card />;
        } else if (creditCards.data.length === 0) {
            return (
                <Empty
                    className="stored-card-empty"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <span style={{ color: black }}>
                            You currently don't have a credit card on file. If you'd like, you can add one below!
                        </span>
                    }
                >
                    <Button type="primary" onClick={() => setVisible(true)}>
                        Add a credit card
                    </Button>
                </Empty>
            );
        } else {
            return creditCards.data.map(creditCard => {
                const expiryDate = dayjs(creditCard.expiry);
                const expiry = expiryDate.format('MM') + ' / ' + expiryDate.format('YY');
                const type = creditCard.type.toUpperCase();
                const cardText = '************' + creditCard.l4d;

                return (
                    <Card key={creditCard.l4d} title="Credit Card">
                        <Row>
                            <Col xs={12} lg={8}>
                                <Text strong>Card Type:</Text>
                            </Col>
                            <Col xs={12} lg={16}>{type}</Col>
                        </Row>
                        <Row>
                            <Col xs={12} lg={8}>
                                <Text strong>Card Number:</Text>
                            </Col>
                            <Col xs={12} lg={16}>{cardText}</Col>
                        </Row>
                        <Row>
                            <Col xs={12} lg={8}>
                                <Text strong>Expiry Date:</Text>
                            </Col>
                            <Col xs={12} lg={16}>{expiry}</Col>
                        </Row>
                        <Row className="authorized-message-padding">
                            <Col span={24}>
                                This member authorized their card for automatic payments.
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <Button type="primary" onClick={() => setVisible(true)}>
                                    Update Card
                                </Button>
                            </Col>
                        </Row>
                    </Card>
                );
            });
        }
    }

    return (
        <Spin spinning={loading}>
            <Row>
                <Col {...cardsGridColumn} style={{ backgroundColor: white }}>
                    {getRenderComponent()}
                </Col>
            </Row>
                <StoreCreditCardForm
                    visible={visible}
                    onCancel={onCancel}
                    onSubmit={onSubmit}
                    formText={getFormText()}
                />
        </Spin>
    )
}

export default StoredCreditCardCard;